import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AppointmentsCard from "./shared/AppointmentCard";
import Cardpatients from "./shared/Cardpatients";

import { Icons1, Icons2, Icons3 } from "../assets/icon/icons";
import { useAuth } from "../context/AuthContext";

/* ================= TYPES ================= */

interface ProviderData {
  name: string;
  title: string;
  email: string;
  specialization: string;
  clinicName: string;
  existingPatients: number;
  newPatients: number;
  alertedPatients: number;
  totalPatients: number;
  maleCount: number;
  femaleCount: number;
  malePercent: number;
  femalePercent: number;
}

interface Referral {
  id: string;
  patientName: string;
  referrerName: string;
  reason: string;
  status: string;
  createdAt: string;
}

interface Appointment {
  id: string;
  patientName: string;
  time: string;
  contact: string;
  status: string;
}

/* ================= HELPERS ================= */

const mapAppointmentStatus = (status?: string) => {
  if (status === "PENDING") return "CONFIRMED";
  return status || "CONFIRMED";
};

const formatTime = (time: string) =>
  new Date(`1970-01-01T${time}`).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

/* ================= SKELETON ================= */

const DashboardSkeleton = () => {
  const skeletonCard: React.CSSProperties = {
    background: "#f1f5f9",
    borderRadius: 8,
    height: 150,
    animation: "pulse 1.5s infinite",
  };

  const skeletonSmall: React.CSSProperties = {
    background: "#f1f5f9",
    borderRadius: 6,
    height: 60,
    animation: "pulse 1.5s infinite",
  };

  return (
    <div className="section">
      <div className="heading" style={{ marginBottom: 20 }}>
        Loading Dashboard...
      </div>

      <div style={{ display: "flex", gap: 20 }}>
        <div style={{ ...skeletonCard, width: "33%" }} />

        <div
          style={{
            width: "33%",
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          <div style={skeletonSmall} />
          <div style={skeletonSmall} />
        </div>

        <div style={{ ...skeletonCard, width: "33%" }} />
      </div>

      <div style={{ display: "flex", gap: 20, marginTop: 20 }}>
        <div style={{ ...skeletonCard, width: "40%", height: 280 }} />
        <div style={{ ...skeletonCard, width: "60%", height: 280 }} />
      </div>

      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

/* ================= COMPONENT ================= */

export const DashboardLayout = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [providerData, setProviderData] = useState<ProviderData | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [noShowCount, setNoShowCount] = useState<number>(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProvider = async () => {
      try {
        const token =
          user?.token || localStorage.getItem("vivo_PROVIDER_token");
        const providerId =
          String(user?.id) || localStorage.getItem("vivo_PROVIDER_id");

        if (!token || !providerId) {
          throw new Error("Missing token or provider id");
        }

        const response = await fetch(
          `https://vivotal-backend.vercel.app/api/v1/providers/${providerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error("Failed to fetch provider data");

        const json = await response.json();
        const providerRaw = json.data;

        setProviderData({
          name: `${providerRaw.first_name} ${providerRaw.last_name}`,
          title: providerRaw.title,
          email: providerRaw.email,
          specialization: providerRaw.specialization,
          clinicName: providerRaw.clinic_name,
          existingPatients: providerRaw.statistics?.existingPatients || 0,
          newPatients: providerRaw.statistics?.newPatients || 0,
          alertedPatients: providerRaw.statistics?.alertedPatients || 0,
          totalPatients: providerRaw.statistics?.totalPatients?.total || 0,
          maleCount: providerRaw.statistics?.totalPatients?.male || 0,
          femaleCount: providerRaw.statistics?.totalPatients?.female || 0,
          malePercent:
            providerRaw.statistics?.totalPatients?.malePercentage || 0,
          femalePercent:
            providerRaw.statistics?.totalPatients?.femalePercentage || 0,
        });

        setAppointments(
          Array.isArray(providerRaw.appointments)
            ? providerRaw.appointments.map((appt: any) => ({
                id: appt.id,
                patientName: appt.patient?.fullName ?? "N/A",
                time: appt.appointmentTime
                  ? formatTime(appt.appointmentTime)
                  : "--",
                contact: appt.patient?.contactNumber ?? "--",
                status: mapAppointmentStatus(appt.status),
              }))
            : []
        );

        const referralsResponse = await fetch(
          `https://vivotal-backend.vercel.app/api/v1/referrals/to-provider/${providerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (referralsResponse.ok) {
          const refJson = await referralsResponse.json();
          setReferrals(
            Array.isArray(refJson.data)
              ? refJson.data.map((ref: any) => ({
                  id: ref.id,
                  patientName:
                    ref.patient?.username ??
                    ref.patient?.email ??
                    "N/A",
                  referrerName: ref.from_provider?.provider
                    ? `${ref.from_provider.provider.first_name} ${ref.from_provider.provider.last_name}`
                    : ref.from_provider?.email ?? "N/A",
                  reason: ref.reason,
                  status: ref.status,
                  createdAt: ref.created_at || ref.createdAt,
                }))
              : []
          );
        }

        const noShowResponse = await fetch(
          `https://vivotal-backend.vercel.app/api/v1/no-show/list?provider_id=${providerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (noShowResponse.ok) {
          const nsJson = await noShowResponse.json();
          setNoShowCount(Array.isArray(nsJson.data) ? nsJson.data.length : 0);
        }
      } catch (err: any) {
        setError(err.message || "Failed to load dashboard");
        toast.error("Session expired. Please login again.");
        logout();
        navigate("/provider-login");
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchProvider();
  }, [user, logout, navigate]);

  if (loading) return <DashboardSkeleton />;
  if (error) return <div style={{ padding: 20, color: "red" }}>{error}</div>;
  if (!providerData) return null;

  return (
    <div className="section">
      <div className="heading" style={{ marginBottom: 20 }}>
        Dashboard
      </div>

      <div style={{ display: "flex", gap: 20 }}>
        <div
          style={{ ...cardStyle, width: "33%" }}
          onClick={() => navigate("/layout/patient")}
        >
          <Header title="Existing Patients" />
          <Center>
            <Icons1 />
            <BigValue>{providerData.existingPatients}</BigValue>
          </Center>
        </div>

        <div
          style={{
            width: "33%",
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          <Cardpatients
            label="New Patients"
            icon={<Icons2 />}
            number={providerData.newPatients}
            linkText="view"
            onLinkClick={() => navigate("/layout/patient")}
          />
          <Cardpatients
            label="Alerted Patients"
            icon={<Icons3 />}
            number={providerData.alertedPatients}
            linkText="view"
            onLinkClick={() => navigate("/layout/alerts")}
          />
        </div>

        <div
          style={{ ...cardStyle, width: "33%" }}
          onClick={() => navigate("/layout/noshow")}
        >
          <Header title="No Show Patients" />
          <Center>
            <Icons1 />
            <BigValue
              style={{
                color: noShowCount > 0 ? "#d32f2f" : "#2e7d32",
              }}
            >
              {noShowCount}
            </BigValue>
          </Center>
        </div>
      </div>

      <div style={{ display: "flex", gap: 20, marginTop: 20 }}>
        <div
          style={{ width: "40%", ...cardStyle }}
          onClick={() => navigate("/layout/referral")}
        >
          <Header title="Referrals Received" />
          <div style={{ paddingTop: 16 }}>
            {referrals.length === 0 ? (
              <div style={{ fontSize: 12, color: "#666" }}>
                No referrals received
              </div>
            ) : (
              referrals.map((ref) => (
                <div
                  key={ref.id}
                  style={{
                    padding: 12,
                    border: "1px solid #e2e8f0",
                    borderRadius: 6,
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    Patient: {ref.patientName}
                  </div>
                  <div style={{ fontSize: 11 }}>
                    From: {ref.referrerName}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{ width: "60%", ...cardStyle }}>
          <AppointmentsCard appointments={appointments} />
        </div>
      </div>
    </div>
  );
};

/* ================= UI HELPERS ================= */

const cardStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: 8,
  border: "1px solid rgba(226,232,240,0.5)",
  padding: 16,
};

const Header = ({ title }: { title: string }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      fontWeight: 700,
    }}
  >
    <div>{title}</div>
    <div style={{ color: "#1976d2", textDecoration: "underline" }}>
      view
    </div>
  </div>
);

const Center = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      height: 120,
      justifyContent: "center",
    }}
  >
    {children}
  </div>
);

const BigValue = ({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <div
    style={{
      marginTop: 16,
      fontSize: 24,
      fontWeight: "bold",
      ...style,
    }}
  >
    {children}
  </div>
);
